import { useEffect, useRef, useState } from "react";
import { searchContacts, type ContactHit } from "../../api";

export interface Recipient {
  email: string;
  name?: string;
}

interface Props {
  label: string;
  recipients: Recipient[];
  onChange: (next: Recipient[]) => void;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Recipient field for Compose — selected addresses as chips, plus a search box
 * that queries the unified contact endpoint (CRM borrowers + lenders, source-
 * badged) AND accepts a free-typed address (Enter / comma / blur). Outlook
 * contacts are intentionally absent for now (would need Contacts.Read).
 */
export function ContactPicker({ label, recipients, onChange }: Props) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<ContactHit[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const myReq = ++reqRef.current;
    const t = setTimeout(async () => {
      try {
        const { contacts } = await searchContacts(q);
        if (myReq === reqRef.current) {
          setHits(contacts);
          setOpen(true);
        }
      } catch {
        if (myReq === reqRef.current) setHits([]);
      } finally {
        if (myReq === reqRef.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const has = (email: string) =>
    recipients.some((r) => r.email.toLowerCase() === email.toLowerCase());

  const add = (r: Recipient) => {
    if (!has(r.email)) onChange([...recipients, r]);
    setQuery("");
    setHits([]);
    setOpen(false);
  };

  const remove = (email: string) =>
    onChange(recipients.filter((r) => r.email.toLowerCase() !== email.toLowerCase()));

  // Commit a free-typed address if it's a valid email.
  const commitFreeType = () => {
    const v = query.trim().replace(/[,;]$/, "").trim();
    if (EMAIL_RE.test(v)) add({ email: v });
  };

  return (
    <div className="contact-picker">
      <span className="contact-picker-label">{label}</span>
      <div className="contact-picker-field">
        <div className="contact-picker-chips">
          {recipients.map((r) => (
            <span key={r.email} className="contact-chip" title={r.email}>
              {r.name || r.email}
              <button
                className="contact-chip-x"
                onClick={() => remove(r.email)}
                title="Remove"
              >
                ✕
              </button>
            </span>
          ))}
          <input
            className="contact-picker-input"
            value={query}
            placeholder={recipients.length === 0 ? "Search name / email, or type an address…" : ""}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                commitFreeType();
              } else if (e.key === "Backspace" && !query && recipients.length) {
                remove(recipients[recipients.length - 1].email);
              }
            }}
            onBlur={() => {
              commitFreeType();
              // Delay so a dropdown click registers before close.
              setTimeout(() => setOpen(false), 150);
            }}
            onFocus={() => hits.length && setOpen(true)}
          />
        </div>

        {open && (searching || hits.length > 0) && (
          <div className="contact-picker-dropdown">
            {searching && <div className="contact-picker-dim">Searching…</div>}
            {hits.map((h) => (
              <button
                key={`${h.source}:${h.email}`}
                className="contact-picker-hit"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add({ email: h.email, name: h.name })}
                disabled={has(h.email)}
              >
                <span className="contact-picker-hit-main">
                  <span className="contact-picker-hit-name">{h.name}</span>
                  <span className="contact-picker-hit-email">{h.email}</span>
                </span>
                <span
                  className={
                    h.source === "lender"
                      ? "contact-badge contact-badge-lender"
                      : "contact-badge contact-badge-borrower"
                  }
                >
                  {h.source === "lender" ? "LENDER" : "BORROWER"}
                  {h.sublabel ? ` · ${h.sublabel}` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
